import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { setSizeBusy } from '@theme/ntw-size-filter';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    if (this.hasAttribute('data-ntw-size-aware') && !this.hasAttribute('data-ntw-size-ready')) {
      setSizeBusy(this, new URL(location.href));
      queueMicrotask(() => {
        if (this.isConnected) sectionRenderer.renderSection(this.getAttribute('section-id'));
      });
    }
    super.connectedCallback();
    if (this.hasAttribute('data-ntw-size-aware')) window.addEventListener('popstate', this.#restoreFilters);

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.#restoreFilters);
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  #restoreFilters = () => sectionRenderer.renderSection(this.getAttribute('section-id'));

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
